/**
 * Kopplung Bildschirm 1 (Guide + Chat) ↔ Bildschirm 2 (Buehnen-Vollbild).
 * Protokoll und Begruendung: ./zweiterBildschirm.ts.
 *
 * Bildschirm 1 (`rolle: 'b1'`): `senden()` liefert `true`, wenn die Buehne
 * hinuebergegangen ist — dann oeffnet er sie NICHT selbst. `false` heisst: selbst
 * oeffnen; ist die Kopplung gewollt, steht der Grund in `hinweis`. Laeuft eine
 * Quittung nicht ein, ruft der Hook `beiRueckfall` — der Aufrufer oeffnet dann
 * nachtraeglich selbst, und `hinweis` sagt es.
 *
 * Bildschirm 2 (`rolle: 'b2'`): `beiAuftrag` fuehrt aus und sagt, ob es ging; die
 * Quittung schickt der Hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AuftragsBuch, LEBENSZEICHEN_FRIST_MS, LEBENSZEICHEN_MS, QUITTUNG_FRIST_MS, fensterKennung, gegenseiteEndgueltigWeg, gegenseiteLebt, oeffneKanal,
  type AuftragsNachricht, type BildschirmNachricht, type Kanal,
} from './zweiterBildschirm';

type OhneNr = Omit<Extract<AuftragsNachricht, { art: 'buehne' }>, 'nr'>
  | Omit<Extract<AuftragsNachricht, { art: 'leeren' }>, 'nr'>
  | Omit<Extract<AuftragsNachricht, { art: 'arbeitsbereich' }>, 'nr'>;

export interface ZweiterBildschirmOptionen {
  person: string;
  rolle: 'b1' | 'b2';
  /** b1: steuert einen zweiten Bildschirm; b2: ist das Buehnen-Vollbild. Aus = kein Kanal. */
  aktiv: boolean;
  /**
   * Bildschirm 2: Auftrag ausfuehren. Darf ein Promise liefern — dann quittiert B2 erst, wenn es
   * erfuellt ist (ASSIOEFFNENR P1: B2 belegt die gezeichnete Buehne an seinem DOM, bevor er ok sagt).
   * Laenger als die Quittungsfrist darf es nicht dauern, sonst holt B1 die Buehne zurueck.
   */
  beiAuftrag?: (n: AuftragsNachricht) => { ok: boolean; fehler?: string } | Promise<{ ok: boolean; fehler?: string }>;
  /** Bildschirm 1: B2 hat einen Auftrag quittiert (ok oder abgelehnt). */
  beiQuittung?: (n: AuftragsNachricht, ok: boolean) => void;
  beiRueckfall?: (n: AuftragsNachricht, grund: string) => void;
  /**
   * Die Gegenseite hat sich nach `anlaufFristMs` nicht (wieder) gemeldet — seit Start
   * oder seit ihrem „weg". Einmal je Ausfall; der Aufrufer kehrt in den Normalzustand
   * zurueck (25.09.2026, siehe `gegenseiteEndgueltigWeg`).
   */
  beiGegenseiteWeg?: () => void;
  /** Anlaufzeit fuer `beiGegenseiteWeg`; Vorgabe LEBENSZEICHEN_FRIST_MS. */
  anlaufFristMs?: number;
}

const STUMM_MS = 10_000;

export function useZweiterBildschirm(o: ZweiterBildschirmOptionen) {
  const [gegenseite, setGegenseite] = useState(false);
  const [kanalFehler, setKanalFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [stumm, setStumm] = useState(false);
  const stummBis = useRef(0);
  const kanal = useRef<Kanal | null>(null);
  const zuletzt = useRef<number | null>(null);
  const buch = useRef(new AuftragsBuch());
  const auftragRef = useRef(o.beiAuftrag); auftragRef.current = o.beiAuftrag;
  const quittungRef = useRef(o.beiQuittung); quittungRef.current = o.beiQuittung;
  const rueckfallRef = useRef(o.beiRueckfall); rueckfallRef.current = o.beiRueckfall;
  const wegRef = useRef(o.beiGegenseiteWeg); wegRef.current = o.beiGegenseiteWeg;
  const anlaufFrist = o.anlaufFristMs ?? LEBENSZEICHEN_FRIST_MS;
  const gekoppeltRef = useRef(false);

  const rueckfall = useCallback((liste: { nachricht: AuftragsNachricht }[], grund: string) => {
    if (!liste.length) return;
    for (const a of liste) rueckfallRef.current?.(a.nachricht, grund);
    if (liste.some(a => a.nachricht.art === 'buehne')) setHinweis(`${grund} — die Bühne ist hier auf Bildschirm 1 geöffnet.`);
  }, []);

  useEffect(() => {
    if (!o.aktiv) { setGegenseite(false); zuletzt.current = null; return; }
    const ich = fensterKennung();
    const eigeneArt = o.rolle === 'b1' ? 'b1-lebt' : 'b2-lebt';
    const fremdeArt = o.rolle === 'b1' ? 'b2-lebt' : 'b1-lebt';
    let anlaufSeit = Date.now();
    let wegGemeldet = false;
    let k: Kanal;
    try {
      k = oeffneKanal(o.person, (n: BildschirmNachricht) => {
        if (n.art === fremdeArt) { zuletzt.current = Date.now(); wegGemeldet = false; setGegenseite(true); return; }
        if (n.art === 'weg' && n.rolle !== o.rolle) {
          zuletzt.current = null; setGegenseite(false);
          anlaufSeit = Date.now();                         // Neuladen der Gegenseite abwarten, dann aufgeben
          if (o.rolle === 'b1') rueckfall(buch.current.alleAustragen(), 'Bildschirm 2 wurde geschlossen');
          return;
        }
        if (o.rolle === 'b1' && n.art === 'quittung') {
          const a = buch.current.quittieren(n.nr);
          if (!a) return;                                  // doppelte oder fremde Quittung
          quittungRef.current?.(a.nachricht, n.ok);
          if (!n.ok) rueckfall([a], `Bildschirm 2 konnte die Bühne nicht öffnen: ${n.fehler ?? 'ohne Grund'}`);
          return;
        }
        if (o.rolle === 'b2' && (n.art === 'buehne' || n.art === 'leeren' || n.art === 'arbeitsbereich')) {
          const gescheitert = (err: unknown) => {
            console.error('[zweiterBildschirm] Auftrag gescheitert:', err);
            return { ok: false, fehler: err instanceof Error ? err.message : String(err) };
          };
          let antwort: { ok: boolean; fehler?: string } | Promise<{ ok: boolean; fehler?: string }>;
          try {
            antwort = auftragRef.current ? auftragRef.current(n) : { ok: false, fehler: 'Bildschirm 2 ist nicht bereit' };
          } catch (err) { antwort = gescheitert(err); }
          void Promise.resolve(antwort).catch(gescheitert).then(a => {
            try { k.senden({ art: 'quittung', nr: n.nr, ...a }); }
            catch (err) { console.error('[zweiterBildschirm] Quittung nicht gesendet:', err); }
          });
        }
      });
    } catch (err) {
      setKanalFehler(err instanceof Error ? err.message : String(err));
      return;
    }
    kanal.current = k;
    setKanalFehler(null);
    const schlag = () => k.senden({ art: eigeneArt, fenster: ich, ts: Date.now() } as BildschirmNachricht);
    schlag();
    const takt = window.setInterval(() => {
      schlag();
      const jetzt = Date.now();
      const lebt = gegenseiteLebt(zuletzt.current, jetzt);
      setGegenseite(lebt);
      if (!wegGemeldet && gegenseiteEndgueltigWeg(anlaufSeit, zuletzt.current, jetzt, anlaufFrist)) {
        wegGemeldet = true;
        wegRef.current?.();
      }
      if (o.rolle !== 'b1') return;
      if (stummBis.current && jetzt >= stummBis.current) { stummBis.current = 0; setStumm(false); }
      if (!lebt && buch.current.anzahl) rueckfall(buch.current.alleAustragen(), 'Bildschirm 2 antwortet nicht mehr');
      const ueberfaellig = buch.current.abgelaufen(jetzt, QUITTUNG_FRIST_MS);
      if (ueberfaellig.length) {
        stummBis.current = jetzt + STUMM_MS; setStumm(true);
        rueckfall(ueberfaellig, `Bildschirm 2 hat nicht innerhalb von ${QUITTUNG_FRIST_MS / 1000} s bestätigt`);
      }
    }, Math.min(LEBENSZEICHEN_MS, 1000));
    const weg = () => k.senden({ art: 'weg', fenster: ich, rolle: o.rolle });
    window.addEventListener('pagehide', weg);
    return () => {
      window.clearInterval(takt);
      window.removeEventListener('pagehide', weg);
      weg();
      k.schliessen();
      kanal.current = null;
      if (o.rolle === 'b1') rueckfall(buch.current.alleAustragen(), 'Kopplung beendet');
    };
  }, [o.aktiv, o.rolle, o.person, rueckfall, anlaufFrist]);

  const gekoppelt = o.aktiv && gegenseite && !kanalFehler && !stumm;
  gekoppeltRef.current = gekoppelt;

  /** Nur Bildschirm 1. Stabil — darf in Callbacks stehen, die der Guide als Abhaengigkeit fuehrt. */
  const senden = useCallback((n: OhneNr): boolean => {
    if (!kanal.current) return false;                  // Kopplung nicht gewollt: still, weil nichts erwartet wird
    if (!gekoppeltRef.current) {
      if (n.art === 'buehne') setHinweis('Bildschirm 2 ist nicht verbunden — die Bühne ist hier auf Bildschirm 1 geöffnet.');
      return false;
    }
    const nachricht = buch.current.eintragen(n, Date.now());
    try {
      kanal.current.senden(nachricht);
    } catch (err) {
      buch.current.quittieren(nachricht.nr);
      console.error('[zweiterBildschirm] Senden gescheitert:', err);
      if (n.art === 'buehne') setHinweis(`Bühne ließ sich nicht an Bildschirm 2 übergeben (${err instanceof Error ? err.message : String(err)}) — sie ist hier geöffnet.`);
      return false;
    }
    if (n.art === 'buehne') setHinweis(null);
    return true;
  }, []);

  return { gekoppelt, gegenseite, kanalFehler, hinweis, hinweisSetzen: setHinweis, hinweisWeg: useCallback(() => { setHinweis(null); stummBis.current = 0; setStumm(false); }, []), senden };
}
